/*
 * Copyright 2024, Corning Optical Communications
 * All Rights Reserved.
 */

/**
 * MA6200-G2 Hierarchy Builder
 * Parses MA6200-G2 DAS inventory data and builds the topology hierarchy
 *
 * ObjectKey Format: {controllerSerial}.{deviceType}.{deviceSerial}
 * Topology Format: {route} (e.g. 0.0.0.0)
 *
 * Route-based Hierarchy:
 * - 0.0.0.0: Root (Primary A3) - label: "MA6200-G2"
 * - 5.0.0.0: Connected to root port 5
 * - 5.2.0.0: Connected to 5.0.0.0 port 2
 *
 * Labels use the full equipment type and last non-zero octet as port (dot-separated):
 * - Primary A3 (root) -> "MA6200-G2"
 * - RU3 + port 3      -> "RU3.3"
 * - E3  + port 5      -> "E3.5"
 * - Secondary A3      -> "A3.2" (or actual devType + port)
 */

import { IInventoryGraphNode, IRemoteEquipment } from "../../../store/api/devices/types";
import { TopologyNode, DasNodeType, DasNodeStatus, HierarchyBuilder } from "../types";

interface ObjectKeyParts {
  targetSerial: string;
  devId?: string;
  route?: string;
}

export class MA6200G2HierarchyBuilder implements HierarchyBuilder {
  private equipmentMap: Map<string, IRemoteEquipment>;
  private equipmentList: IRemoteEquipment[];
  private processedNodes: Set<string>;

  constructor() {
    this.equipmentMap = new Map();
    this.equipmentList = [];
    this.processedNodes = new Set();
  }

  setEquipmentData(equipment: IRemoteEquipment[]) {
    this.equipmentMap.clear();
    this.equipmentList = [];

    const seen = new Set<string>();
    equipment.forEach((eq) => {
      const dedupKey = eq.objectKey || eq.serialNumber || `${eq.equipmentType}-${eq.hostname}-${eq.ipAddress}`;
      if (!seen.has(dedupKey)) {
        this.equipmentList.push(eq);
        seen.add(dedupKey);
      }
    });

    console.log('[MA6200-G2] Setting equipment data:', equipment.length, 'items');

    equipment.forEach((eq) => {
      if (eq.objectKey) {
        this.equipmentMap.set(eq.objectKey, eq);
      }
      if (eq.serialNumber) {
        this.equipmentMap.set(eq.serialNumber, eq);
      }
    });

    console.log('[MA6200-G2] Equipment map size:', this.equipmentMap.size);
  }

  private normalizeRoute(route?: string): string {
    const raw = (route || '').trim();
    if (!raw) return '';
    if (raw === '0') return '0.0.0.0';
    if (/^\d+$/.test(raw)) return `${raw}.0.0.0`;

    const parts = raw.split('.');
    if (parts.every((p) => /^\d+$/.test(p)) && parts.length < 4) {
      return [...parts, ...Array(4 - parts.length).fill('0')].join('.');
    }

    return raw;
  }

  private parseDxruTopology(topology?: string): { deuKey: string; portPart: string } {
    const raw = (topology || '').trim();
    if (!raw) {
      return { deuKey: '', portPart: '' };
    }

    const slashIndex = raw.indexOf('/');
    if (slashIndex === -1) {
      return { deuKey: '', portPart: raw };
    }

    return {
      deuKey: raw.slice(0, slashIndex).trim(),
      portPart: raw.slice(slashIndex + 1).trim(),
    };
  }

  private normalizeDeuDisplay(topology?: string): string {
    const raw = (topology || '').trim();
    if (!raw) return '0';
    return raw.replace(/^DEU\s*/i, '').trim() || '0';
  }

  private normalizeRiuDisplay(topology?: string): string {
    const raw = (topology || '').trim();
    if (!raw) return '0';
    return raw.replace(/^RIU\s*/i, '').trim() || '0';
  }

  private parseObjectKey(objectKey: string): ObjectKeyParts {
    if (!objectKey) {
      return { targetSerial: '' };
    }
    const parts = objectKey.split(".");
    const result: ObjectKeyParts = { targetSerial: parts[0] || '' };
    if (parts.length > 2) result.devId = parts[2];
    const equipment = this.equipmentMap.get(objectKey);
    if (equipment?.topology) {
      result.route = equipment.topology;
    }
    return result;
  }

  private isPrimaryA3(objectKey: string): boolean {
    const parts = this.parseObjectKey(objectKey);
    const equipment = this.equipmentMap.get(objectKey);
    const equipmentType = (equipment?.equipmentType || '').toUpperCase();
    const route = this.normalizeRoute(parts.route);
    if (!equipmentType.includes('A3')) {
      return false;
    }

    return route === '0.0.0.0';
  }

  parseStatus(status: string): DasNodeStatus {
    const statusLower = (status || "").toLowerCase();
    switch (statusLower) {
      case "connected":
      case "online":
      case "normal":
        return DasNodeStatus.ONLINE;
      case "disconnected":
      case "offline":
        return DasNodeStatus.DISCONNECTED;
      case "clear":
      case "cleared":
        return DasNodeStatus.CLEAR;
      case "minor":
        return DasNodeStatus.MINOR;
      case "major":
      case "warning":
        return DasNodeStatus.MAJOR;
      case "critical":
      case "error":
        return DasNodeStatus.CRITICAL;
      default:
        return DasNodeStatus.UNKNOWN;
    }
  }

  parseNodeType(node: IInventoryGraphNode): DasNodeType {
    const equipmentType = node.equipmentType || "";
    const normalized = equipmentType.toUpperCase();
  
    if (normalized.includes("DCU")) {
      return DasNodeType.SITE;
    }
    if (normalized.includes("RIU")) {
      return DasNodeType.RAU;
    }
    if (normalized.includes("DXRU")) {
      return DasNodeType.OCH;
    }
    if (normalized.includes("DEU")) {
      return DasNodeType.OCH;
    }
    return DasNodeType.SITE;
  }

  private createTopologyNode(equipment: IRemoteEquipment, type: DasNodeType, isRoot: boolean = false): TopologyNode {
    let label = "";

    if (isRoot) {
      // Root (primary A3) shows MA6200-G2 label in topology view
      label = "MA6200-G2";
      console.log('[MA6200-G2] Creating ROOT node with label:', label);
    } else {
      if (equipment.objectKey) {
        const parts = this.parseObjectKey(equipment.objectKey);
        if (parts.route) {
          const normalizedRoute = this.normalizeRoute(parts.route);
          const routeParts = normalizedRoute.split(".");
          const nonZeroRouteParts = routeParts.filter((part) => part !== "0");

          const equipmentType = (equipment.equipmentType || '').toUpperCase();

          if (equipmentType.includes('A3') || equipmentType.includes('E3')) {
            label = equipmentType;
          }

          let port: string | null = null;
          for (let i = routeParts.length - 1; i >= 0; i--) {
            if (routeParts[i] !== "0") {
              port = routeParts[i];
              break;
            }
          }

          if (port && !label) {
            const fullEquipmentType = equipment.equipmentType || "DEVICE";
            label = `${fullEquipmentType}.${port}`;
            console.log(`[MA6200-G2] Creating node for ${equipment.objectKey}: label="${label}", type="${fullEquipmentType}", route="${parts.route}"`);
          }
        }
      }

      if (!label) {
        label = equipment.equipmentType || "Unknown";
      }
    }

    return {
      id: equipment.objectKey || equipment.serialNumber || "",
      label,
      type,
      equipmentType: equipment.equipmentType || "",
      status: this.parseStatus(equipment.status || ""),
      model: equipment.model,
      ipAddress: equipment.ipAddress,
      serialNumber: equipment.serialNumber,
      managedObject: equipment.objectKey,
      children: [],
      metadata: { equipment },
    };
  }

  buildHierarchy(_nodes: IInventoryGraphNode[], _edges: any[], rootId: string): TopologyNode | null {
    const equipment = this.equipmentList;
    this.processedNodes.clear();

    if (equipment.length === 0) {
      console.log('[MA6200-G2] No equipment data available');
      return null;
    }

    console.log('[MA6200-G2] Building hierarchy from', equipment.length, 'equipment items');

    let primaryA3 = equipment.find((eq) => eq.objectKey && this.isPrimaryA3(eq.objectKey));

    if (!primaryA3) {
      console.log('[MA6200-G2] No A3 device with route 0.0.0.0 found');
      return null;
    }

    console.log('[MA6200-G2] Found primary A3:', primaryA3.objectKey);

    const root = this.createTopologyNode(primaryA3, DasNodeType.SITE, true);

    if (primaryA3.objectKey) {
      this.processedNodes.add(primaryA3.objectKey);
    }

    this.buildChildren(root, equipment);

    console.log('[MA6200-G2] Hierarchy built with', root.children.length, 'direct children');
    return root;
  }

  /**
 * Determines whether a given child route is a direct child of a parent route.
 *
 * Rules:
 * - Child must match all non-zero levels of the parent.
 * - Child must introduce exactly ONE new non-zero level.
 * - The new level must appear AFTER the parent’s deepest non-zero level.
 *
 *   Examples:
 * - Parent: 0.0.0.0, Child: 5.0.0.0 → true  (root → first level)
 * - Parent: 5.0.0.0, Child: 5.2.0.0 → true  (next level under same branch)
 * - Parent: 1.0.5.0, Child: 1.0.5.6 → true  (deeper level added at index 3)
 * - Parent: 1.0.0.0, Child: 1.0.13.0 → true (sparse depth, still valid direct child)
 * - Parent: 1.1.0.0, Child: 1.1.1.0 → true  (next depth)
 * - Parent: 1.0.1.0, Child: 1.1.1.0 → false (branch mismatch at index 1)
 * - Parent: 5.0.0.0, Child: 5.2.3.0 → false (adds more than one new level)
 * - Parent: 5.0.0.0, Child: 3.0.0.0 → false (different root branch)
 * - Parent: 1.2.0.0, Child: 1.3.0.0 → false (parent level changed)
 * - Parent: 1.2.0.0, Child: 1.2.0.0 → false (same route, not a child)
 */
  private isDirectChild(parentRoute: string, childRoute: string): boolean {
  if (!parentRoute || !childRoute || parentRoute === childRoute) return false;

  const parentOctets = parentRoute.split(".");
  const childOctets = childRoute.split(".");

  if (parentOctets.length !== 4 || childOctets.length !== 4) return false;

  let parentLastNonZeroIndex = -1;   // deepest level used in parent
  let childNewLevelIndex = -1;       // index where child introduces new level
  let childNewLevelCount = 0;        // how many new levels child adds

  for (let index = 0; index < 4; index++) {
    const parentValue = parentOctets[index];
    const childValue = childOctets[index];

    // Parent has a value → child must match exactly
    if (parentValue !== "0") {
      if (parentValue !== childValue) return false;
      parentLastNonZeroIndex = index;
      continue;
    }

    // Parent is empty but child has value → new level introduced
    if (childValue !== "0") {
      childNewLevelCount++;
      if (childNewLevelIndex === -1) {
        childNewLevelIndex = index;
      }
    }
  }

  // Child must introduce exactly one new level
  if (childNewLevelCount !== 1) return false;

  // New level must be deeper than parent’s last level
  return childNewLevelIndex > parentLastNonZeroIndex;
}


  private buildChildren(parent: TopologyNode, equipment: IRemoteEquipment[]) {
    const parentObjectKey = parent.managedObject;
    if (!parentObjectKey) return;

    const parentParts = this.parseObjectKey(parentObjectKey);
    const normalizedParentRoute = this.normalizeRoute(parentParts.route);

    if (!normalizedParentRoute) return;

    const children = equipment.filter((eq) => {
      if (eq.objectKey === parentObjectKey) return false;
      if (eq.objectKey) {
        const childParts = this.parseObjectKey(eq.objectKey);
        if (childParts.targetSerial !== parentParts.targetSerial) return false;
        if (childParts.route) {
          return this.isDirectChild(normalizedParentRoute, this.normalizeRoute(childParts.route));
        }
      }
      return false;
    });

    console.log(`[MA6200-G2] Found ${children.length} children for ${parentObjectKey} (route: ${parentParts.route})`);

    children.forEach((childEq) => {
      if (!childEq.objectKey || this.processedNodes.has(childEq.objectKey)) {
        return;
      }
      this.processedNodes.add(childEq.objectKey);

      const dummyNode: IInventoryGraphNode = {
        id: childEq.objectKey || "",
        label: childEq.hostname || "",
        equipmentType: childEq.equipmentType || "",
      };
      const nodeType = this.parseNodeType(dummyNode);
      const childNode = this.createTopologyNode(childEq, nodeType);
      parent.children.push(childNode);
      this.buildChildren(childNode, equipment);
    });

    parent.children.sort((a, b) => {
      const aKey = a.managedObject || "";
      const bKey = b.managedObject || "";
      const aParts = this.parseObjectKey(aKey);
      const bParts = this.parseObjectKey(bKey);

      if (aParts.route && bParts.route) {
        const aOctets = aParts.route.split(".");
        const bOctets = bParts.route.split(".");
        for (let i = 0; i < 4; i++) {
          const aVal = parseInt(aOctets[i] || "0");
          const bVal = parseInt(bOctets[i] || "0");
          if (aVal !== bVal) return aVal - bVal;
        }
      }

      return aKey.localeCompare(bKey);
    });
  }
}

export const ma6200g2HierarchyBuilder = new MA6200G2HierarchyBuilder();
